import * as React from "react";
import { useState, useRef, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

interface EditableCellProps {
  value: string | number | null | undefined;
  onSave: (val: string) => Promise<void>;
  type?: "text" | "number";
  displayFormat?: (val: any) => React.ReactNode;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
}

export function EditableCell({
  value,
  onSave,
  type = "text",
  displayFormat,
  disabled = false,
  className,
  inputClassName,
}: EditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  // Optimistic: keep the last saved value to prevent parent re-renders from reverting display
  const [optimisticValue, setOptimisticValue] = useState<string | number | null | undefined>(undefined);
  const savingRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // When the parent value changes AND we're not in the middle of saving, accept it
  useEffect(() => {
    if (!savingRef.current) {
      setOptimisticValue(undefined);
    }
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = useCallback(() => {
    if (disabled) return;
    const current = optimisticValue !== undefined ? optimisticValue : value;
    setEditValue(String(current ?? ""));
    setEditing(true);
  }, [value, optimisticValue, disabled]);

  const cancel = useCallback(() => {
    setEditing(false);
    setEditValue("");
  }, []);

  const save = useCallback(async () => {
    if (!editing) return;
    const raw = editValue;
    setEditing(false);
    setEditValue("");
    // Hold the optimistic value so parent re-renders don't revert display
    setOptimisticValue(raw);
    savingRef.current = true;
    try {
      await onSave(raw);
    } catch (err: any) {
      // Revert optimistic value on error
      setOptimisticValue(undefined);
      toast({ title: "Error al guardar", description: err?.message ?? "Error desconocido", variant: "destructive" });
    } finally {
      savingRef.current = false;
      // Clear optimistic value so next parent update takes effect
      setOptimisticValue(undefined);
    }
  }, [editing, editValue, onSave, toast]);

  if (editing) {
    return (
      <Input
        ref={inputRef}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") cancel();
        }}
        onBlur={save}
        className={cn("h-7 text-xs min-w-[70px] font-mono", inputClassName)}
      />
    );
  }

  // Use optimistic value if we're in the middle of saving
  const displayValue = optimisticValue !== undefined ? optimisticValue : value;
  const displayed = displayFormat ? displayFormat(displayValue) : (displayValue ?? "—");

  if (disabled) {
    return <>{displayed}</>;
  }

  return (
    <span
      onClick={startEdit}
      className={cn(
        "cursor-pointer border-b border-dashed border-muted-foreground/30 hover:border-primary/60 hover:bg-muted/50 transition-colors rounded-sm px-0.5 -mx-0.5 inline-block",
        className,
      )}
      title="Clic para editar"
    >
      {displayed}
    </span>
  );
}
